<script setup lang="ts">
import { Dialog, Button } from 'primevue';
import { computed, ref, watch } from 'vue';
import type { ProblemDetails } from 'shared/problems/domain/problemDetails';
import ProblemDetailsView from 'shared/problems/components/ProblemDetailsView.vue';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import { storeToRefs } from 'pinia';
import { useDeleteCoil } from '../api/deleteCoil';
import { useCoilStore } from 'shared/coils/stores/coilStore';
import { Assert } from 'shared/assert';

defineExpose({
  show
});

const languageStore = useLanguageStore();
const { translations } = storeToRefs(languageStore);
const dialogTranslations = computed(() => translations.value.coils.deleteDialog);

const coilStore = useCoilStore();
const coilId = ref<string>();
const coilName = ref<string>('');
const visible = ref(false);
const loading = ref(false);
const problems = ref<ProblemDetails>();

const deleteCoil = useDeleteCoil();

watch(coilId, (newId) => {
  if (!newId) {
    visible.value = false;
  }
});

async function show(id: string) {
  try {
    problems.value = undefined;
    loading.value = true;

    const coil = await coilStore.getById(id);
    Assert.definied(coil);
    coilId.value = coil.id;
    coilName.value = coil.name;

    visible.value = true;
  } finally {
    loading.value = false;
  }
}

async function executeDelete() {
  const id = coilId.value;
  if (!id) throw new Error();
  try {
    loading.value = true;
    const response = await deleteCoil.execute({ coilId: id});

    if (response.ok) {
      coilId.value = undefined;
      visible.value = false;
    } else {
      problems.value = response.details;
    }

  } finally {
    loading.value = false;
  }
}

function onHide() {
  coilId.value = undefined;
  visible.value = false;
}

</script>

<template>
  <Dialog modal @hide="onHide" :header="dialogTranslations.header(coilName)" v-model:visible="visible">
    <ProblemDetailsView v-model:model-value="problems"></ProblemDetailsView>
    <div class="flex items-center gap-4">
      <i class="pi pi-exclamation-triangle !text-3xl" style="color: var(--p-red-400)"></i>
      <span>{{ dialogTranslations.description }}</span>
    </div>
    <template #footer>
      <Button :label="dialogTranslations.deleteButton" icon="pi pi-check" @click="executeDelete"></Button>
      <Button :label="translations.generic.cancel" icon="pi pi-times" text @click="onHide"></Button>
    </template>
  </Dialog>
</template>
